import { useEffect, useState } from "react";
import moment from "moment";
import { GET_MISSION_ROLE_EVENTS, GET_PROFILE_EVENTS } from "graphql/queries";
import { useQueryData } from "hooks";
import { getTransformedFilters, TIMELINE_LIST_FILTERS_CONFIG } from "constants/index";

const QUERIES = {
  profileEvents: GET_PROFILE_EVENTS,
  missionRoleEvents: GET_MISSION_ROLE_EVENTS,
};

export default function useTimeline({
  resultsPerPage = 100,
  resourceId,
  advancedFilters = {},
  keyword,
  paginationType,
  sortKey,
  sortDirection,
  keyName = "profileEvents",
  resourceKeyName = "profileId",
}) {
  const [dataByDate, setDataByDate] = useState([]);
  const [storedTotalCount, setStoredTotalCount] = useState(0);
  const { loadingMore, hasNextPage, hasPreviousPage, handleNextPage, handlePreviousPage, loading, data } = useQueryData(
    {
      queryName: QUERIES[keyName],
      keyName,
      resultsPerPage,
      paginationType,
      variables: {
        sortKey,
        sortDirection,
        [resourceKeyName]: resourceId,
        filters: {
          keyword,
        },
        advancedFilters: getTransformedFilters(TIMELINE_LIST_FILTERS_CONFIG, advancedFilters),
        resultFilterMetadata: Object.keys(TIMELINE_LIST_FILTERS_CONFIG),
      },
    }
  );

  useEffect(() => {
    if (data?.[keyName]?.nodes?.length > 0) {
      const groups = data?.[keyName]?.nodes?.reduce((groups, item) => {
        const date = moment(item.createdAt).format("YYYY-MM-DD");

        if (!groups[date]) {
          groups[date] = [];
        }

        const itemExists = groups[date].some((groupItem) => groupItem.uuid === item.uuid);

        if (!itemExists) {
          groups[date].push(item);
        }

        return groups;
      }, {});

      const groupByDate = Object.keys(groups).map((date) => {
        return {
          date,
          items: groups[date],
        };
      });

      setDataByDate(groupByDate);
    }
  }, [data?.[keyName]?.nodes]);

  useEffect(() => {
    if (loading && !data?.[keyName]?.nodes) {
      setDataByDate([]);
    }
  }, [loading, data?.[keyName]?.nodes]);

  const updatedTotalCount = data?.[keyName]?.totalCount;

  useEffect(() => {
    if (typeof updatedTotalCount !== "undefined") {
      setStoredTotalCount(updatedTotalCount);
    }
  }, [updatedTotalCount]);

  return {
    filterMetadata: data?.[keyName]?.filterMetadata,
    loading,
    data: data?.[keyName]?.nodes,
    dataByDate,
    handleNextPage,
    handlePreviousPage,
    hasNextPage,
    hasPreviousPage,
    loadingMore,
    pageInfo: data?.[keyName]?.pageInfo,
    currentPage: data?.[keyName]?.currentPage,
    resultsPerPage: data?.[keyName]?.perPage,
    totalCount: storedTotalCount,
  };
}
